import { Table, useApiQuery, type Column, type Transaction } from 'grabbitbot-legacy-ui'
import { paymentClient } from '../../api'
import {
  TRANSACTION_CAUSE_LABELS,
  TRANSACTION_MODE_LABELS,
  TRANSACTION_RESULT_LABELS,
  TRANSACTION_STATUS_LABELS,
  TRANSACTION_TYPE_LABELS,
  declineMessage,
} from './paymentFlow'
import { DateTime, PaymentPage } from './paymentShared'
import { useState } from 'react'
import { failureText } from '../../api/errorMessages'

function when(t: Transaction): string {
  return t.resultAt || t.createdAt || ''
}

/**
 * `/payment/transactions` — legacy `PaymentTransactionListComponent`: every
 * payment transaction (date, fee type, amount, status) with a details row
 * (what was bought, cause, mode, result, decline code, initiator). A failed
 * transaction also shows the legacy card-decline explanation.
 */
export function PaymentTransactionsPage() {
  const { data, loading, error } = useApiQuery(() => paymentClient.getTransactions(), [])
  const rows = Array.isArray(data) ? data : []
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set())
  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const columns: Column<Transaction>[] = [
    { key: 'date', label: 'Date', align: 'left', sortValue: (r) => when(r), format: (_v, r) => <DateTime value={when(r) || null} /> },
    { key: 'type', label: 'Fee', align: 'left', sortValue: (r) => r.type ?? '', format: (_v, r) => TRANSACTION_TYPE_LABELS[r.type ?? ''] ?? r.type ?? '' },
    {
      key: 'amount',
      label: 'Amount',
      align: 'right',
      numeric: true,
      sortValue: (r) => r.amount ?? 0,
      format: (_v, r) => `${(r.currency ?? '').toUpperCase()} ${r.amountFormatted ?? ''}`.trim(),
    },
    {
      key: 'status',
      label: 'Status',
      align: 'left',
      sortValue: (r) => r.status ?? -1,
      format: (_v, r) => TRANSACTION_STATUS_LABELS[r.status ?? -1] ?? String(r.status ?? ''),
    },
    {
      key: 'details',
      label: '',
      align: 'right',
      sortable: false,
      format: (_v, r) => (
        <button type="button" className="portal-button-link" onClick={() => toggle(r.id ?? '')}>
          {open.has(r.id ?? '') ? 'close' : 'details'}
        </button>
      ),
    },
  ]

  return (
    <PaymentPage title="Transactions">
      {loading && <p>Loading transactions…</p>}
      {error && (
        <p className="portal-notice portal-notice--error" role="alert">
          {failureText(error, 'Could not load transactions')}
        </p>
      )}
      {!loading && !error && rows.length === 0 && <p className="portal-text-muted">No transactions.</p>}
      {rows.length > 0 && (
        <section className="portal-card">
          <div className="portal-table-scroll">
            <Table
              columns={columns}
              rows={rows}
              rowKey={(r, i) => r.id ?? i}
              rowClassName={(r) => (r.status === 9 ? 'portal-row--failed' : r.status === 6 ? 'portal-row--muted' : undefined)}
              renderExpanded={(r) =>
                open.has(r.id ?? '') ? (
                  <tr>
                    <td colSpan={5}>
                      <TransactionDetails transaction={r} />
                    </td>
                  </tr>
                ) : null
              }
            />
          </div>
        </section>
      )}
    </PaymentPage>
  )
}

function TransactionDetails({ transaction: t }: { transaction: Transaction }) {
  const names = (list: { name: string | null }[] | null) => (list ?? []).map((x) => x.name).filter(Boolean).join(', ')
  return (
    <ul className="portal-detail-list">
      {t.type === 'plan' && (
        <li>
          <strong>Subscription</strong>: {names(t.planList)}
        </li>
      )}
      {t.type === 'product' && (
        <li>
          <strong>Product payment</strong>: {names(t.productList)}
        </li>
      )}
      {t.type === 'custom' && t.customSource && (
        <li>
          <strong>Payment source</strong>: {t.customSource}
        </li>
      )}
      {t.type === 'custom' && t.customId && (
        <li>
          <strong>Item ID</strong>: {t.customId}
        </li>
      )}
      {t.type === 'custom' && t.customName && (
        <li>
          <strong>Item Name</strong>: {t.customName}
        </li>
      )}
      <li>
        <strong>Cause</strong>: {TRANSACTION_CAUSE_LABELS[t.cause ?? ''] ?? t.cause ?? ''}
      </li>
      <li>
        <strong>Mode</strong>: {TRANSACTION_MODE_LABELS[t.mode ?? ''] ?? t.mode ?? ''}
      </li>
      <li>
        <strong>Result</strong>: {TRANSACTION_RESULT_LABELS[t.result ?? -1] ?? String(t.result ?? '')}
      </li>
      {t.details?.declineCode && (
        <li>
          <strong>Decline code</strong>: {t.details.declineCode}
        </li>
      )}
      {t.status === 9 && (
        <li className="portal-pnl-negative">
          <strong>Why it failed</strong>: {declineMessage(t)}
        </li>
      )}
      <li>
        <strong>Initiated by:</strong> {t.source ? t.source : 'user'}
      </li>
    </ul>
  )
}
